import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { isPrerendered } from "../lib/hydration";
import { BUSINESS, getOrganizationSchema, getSameAsUrls } from "../lib/businessInfo";
import { PARTNERSHIP_AGREEMENT_SECTIONS } from "../lib/partnershipAgreementContent";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const jsonLd = getOrganizationSchema({
  url: "https://yogaretreatsbooking.com/partnership-agreement",
  description:
    "Partnership and listing agreement for yoga retreat operators listing programs on Yoga Retreats Booking.",
  sameAs: getSameAsUrls(),
});

function PartnershipAgreement() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Partnership / Listing Agreement"
        description={`Terms for retreat operators listing on ${BUSINESS.tradeName}. ${BUSINESS.commissionRate} commission, payment flow, listing requirements, and partner obligations.`}
        canonicalPath="/partnership-agreement"
        jsonLd={jsonLd}
      />

      <section className="relative pt-16 overflow-hidden bg-gradient-to-br from-teal-900 via-emerald-800 to-teal-700">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <motion.div initial={isPrerendered ? false : "hidden"} animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/30 text-emerald-100 text-sm font-medium mb-4 border border-emerald-400/40">
              <FileText size={15} />
              For retreat operators
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Partnership / Listing Agreement
            </h1>
            <p className="text-emerald-100/90 text-lg leading-relaxed max-w-2xl mx-auto">
              Standard terms for listing yoga retreats, wellness programs, and teacher training courses on{" "}
              {BUSINESS.website.replace("https://", "")}.
            </p>
          </motion.div>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-12 bg-white"
          style={{ clipPath: "ellipse(55% 100% at 50% 100%)" }}
        />
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 sm:p-8 mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Platform contact</h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            <span className="font-semibold text-gray-900">Operational address: </span>
            {BUSINESS.operationalAddress.display}
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <span className="font-semibold text-gray-900">Email: </span>
            <a href={`mailto:${BUSINESS.email}`} className="text-emerald-700 hover:underline">
              {BUSINESS.email}
            </a>
            {" · "}
            <span className="font-semibold text-gray-900">WhatsApp: </span>
            {BUSINESS.phoneDisplay}
          </p>
        </div>

        <article className="space-y-10">
          {PARTNERSHIP_AGREEMENT_SECTIONS.map((section) => (
            <section key={section.title} className="border-b border-gray-100 pb-10 last:border-b-0">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>

              {section.rows && (
                <dl className="space-y-3">
                  {section.rows.map(([label, value]) => (
                    <div key={label} className="grid sm:grid-cols-[11rem_1fr] gap-2 text-sm">
                      <dt className="font-semibold text-gray-600">{label}</dt>
                      <dd className="text-gray-800 leading-relaxed">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {section.paragraphs && (
                <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>

        <div className="mt-12 rounded-2xl bg-gray-900 text-white p-8 text-center">
          <h2 className="text-xl font-bold mb-2">Want to list your retreat?</h2>
          <p className="text-gray-300 text-sm mb-6 max-w-lg mx-auto">
            Contact us at {BUSINESS.email} or WhatsApp {BUSINESS.phoneDisplay} to discuss listing your programs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 font-semibold transition-colors"
          >
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default PartnershipAgreement;
